import { useCallback, useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSearch } from "@/hooks/useSearch";
import { getNavFor } from "@/lib/nav";
import { PdfViewer } from "@/components/PdfViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Search, Loader2, Zap, Clock, BookOpen, Globe, FileText,
  ArrowUpRight, History, FlaskConical, RefreshCw, X, AlertCircle, FileSearch, Library,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

const SUGGESTIONS = [
  "Contraindications of fluconazole",
  "Amoxicillin dose",
  "Metformin standard dosage",
  "First-line treatment of uncomplicated malaria",
  "Paracetamol maximum daily dose",
];

function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }

function Highlighted({ text, terms }) {
  const esc = (terms || []).map(escapeRe).filter(Boolean);
  if (!esc.length) return <>{text}</>;
  const re = new RegExp(`(${esc.join("|")})`, "gi");
  const set = new Set(esc.map((e) => e.toLowerCase()));
  const parts = text.split(re);
  return parts.map((p, i) =>
    set.has(p.toLowerCase())
      ? <mark key={i} className="rounded bg-sky-100 px-0.5 font-semibold text-sky-900 dark:bg-sky-900/70 dark:text-sky-100">{p}</mark>
      : <span key={i}>{p}</span>
  );
}

export default function DispenserSearch() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const testMode = params.get("test") === "1";
  const initialQ = params.get("q") || "";
  const [recent, setRecent] = useState([]);
  const [resources, setResources] = useState([]);
  const [excerpt, setExcerpt] = useState(null);
  const [pdfView, setPdfView] = useState(null);

  const resMap = useMemo(() => Object.fromEntries(resources.map((r) => [r.id, r])), [resources]);
  const activeResources = useMemo(() => resources.filter((r) => r.is_active), [resources]);

  const loadSide = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([api.get("/search/recent"), api.get("/resources")]);
      setRecent(r1.data);
      setResources(r2.data);
    } catch (error) {
      console.error("Failed to load sidebar data:", error);
    }
  }, []);

  const { query, setQuery, loading, result, error, runSearch, clear } = useSearch(loadSide);

  useEffect(() => { loadSide(); }, [loadSide]);
  useEffect(() => { if (initialQ) runSearch(initialQ); }, [initialQ, runSearch]);

  const hasPdf = (b) => !!(b.resource_id && resMap[b.resource_id]?.storage_path);

  const openPage = (b) => {
    if (hasPdf(b)) {
      setPdfView({ resourceId: b.resource_id, page: b.page, pdfPage: b.pdf_page, title: b.title });
    } else {
      setExcerpt({ title: b.title, page: b.page, section: b.section, text: b.text });
    }
  };

  const nav = getNavFor(user);
  const showTest = user?.role === "super_admin" || user?.role === "hod";

  return (
    <Layout nav={nav} showTestButton={showTest && !testMode}>
      {testMode && (
        <div className="mb-6 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300" data-testid="test-mode-banner">
          <FlaskConical className="h-4 w-4" /> Test Dispenser View — you are previewing exactly what a branch dispenser sees.
        </div>
      )}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="mb-6">
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Ask about any drug or disease
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Dosage · Interactions · Contraindications · Management — the exact text from your reference books, with page citations.
            </p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); runSearch(query); }} className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. contraindications of fluconazole"
              className="h-14 rounded-2xl pl-12 pr-32 text-base shadow-sm"
              data-testid="dispenser-search-input"
            />
            <Button type="submit" disabled={loading} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl" data-testid="dispenser-search-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Search</>}
            </Button>
          </form>

          <div className="mt-3 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => runSearch(s)} data-testid={`suggestion-${s.slice(0, 10)}`}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-sky-300 hover:text-sky-700">
                {s}
              </button>
            ))}
          </div>

          {/* Loading */}
          {loading && (
            <Card className="mt-8 flex items-center gap-3 p-6 text-sm text-muted-foreground" data-testid="search-loading">
              <Loader2 className="h-5 w-5 animate-spin text-sky-600" />
              Searching your reference books…
            </Card>
          )}

          {/* Error */}
          {error && !loading && (
            <Card className="mt-8 flex flex-col gap-3 p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="search-error">
              <div className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
              </div>
              <Button variant="outline" size="sm" onClick={() => runSearch(query)} data-testid="search-retry-button">
                <RefreshCw className="mr-1.5 h-4 w-4" /> Retry
              </Button>
            </Card>
          )}

          {/* Result */}
          {result && !loading && (
            <div className="mt-8 space-y-5" data-testid="search-result">
              <div className="flex flex-wrap items-center gap-2">
                {result.cached ? (
                  <Badge className="gap-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300" data-testid="cache-badge-hit">
                    <Zap className="h-3 w-3" /> Instant · pooled{result.hit_count ? ` · reused ${result.hit_count}×` : ""}
                  </Badge>
                ) : (
                  <Badge className="gap-1 bg-sky-100 text-sky-700 hover:bg-sky-100 dark:bg-sky-950 dark:text-sky-300" data-testid="cache-badge-fresh">
                    <Library className="h-3 w-3" /> From your reference books
                  </Badge>
                )}
                <Badge variant="outline" className="gap-1 font-mono-c text-xs">
                  <Clock className="h-3 w-3" /> {result.response_ms} ms
                </Badge>
                <div className="ml-auto flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => runSearch(result.query, { fresh: true })} data-testid="search-fresh-button">
                    <RefreshCw className="mr-1.5 h-4 w-4" /> Search fresh
                  </Button>
                  <Button variant="ghost" size="sm" onClick={clear} data-testid="new-search-button">
                    <X className="mr-1.5 h-4 w-4" /> New search
                  </Button>
                </div>
              </div>

              {/* Not found */}
              {result.not_found ? (
                <Card className="p-6 sm:p-8" data-testid="no-match">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                      <FileSearch className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-heading text-base font-bold text-foreground">This isn't in your references yet</div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Nothing in the active reference books matched “{result.query}”. Try one of the closest topics below, or ask an HOD to add the reference.
                      </p>
                    </div>
                  </div>
                  {result.suggestions?.length > 0 && (
                    <div className="mt-4 space-y-1.5 border-t border-border pt-4">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Closest in your books</div>
                      {result.suggestions.map((s, i) => (
                        <button key={i} onClick={() => runSearch(s)} data-testid={`suggestion-close-${i}`}
                          className="block w-full truncate rounded-lg px-2.5 py-2 text-left text-sm text-sky-700 transition-colors hover:bg-sky-50 dark:text-sky-300 dark:hover:bg-sky-950">
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </Card>
              ) : (
                <>
                  <div className="space-y-4">
                    {result.blocks.map((b) => (
                      <Card key={b.id} className="p-5 sm:p-6" data-testid={`result-block-${b.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <BookOpen className="h-4 w-4 text-sky-600" />
                            <span className="font-heading text-sm font-bold text-foreground">{b.section || b.title}</span>
                          </div>
                          {b.page != null && (
                            <Button variant="outline" size="sm" onClick={() => openPage(b)} data-testid={`open-page-${b.id}`}>
                              <FileText className="mr-1.5 h-3.5 w-3.5" /> Open page {b.page}
                            </Button>
                          )}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {b.title}{b.page != null ? ` · printed p.${b.page}` : ""}
                        </div>
                        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                          <Highlighted text={b.text} terms={result.terms} />
                        </p>
                      </Card>
                    ))}
                  </div>

                  {/* Supplementary approved websites */}
                  {result.web?.length > 0 && (
                    <Card className="p-5" data-testid="web-sources">
                      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        <Globe className="h-3.5 w-3.5 text-emerald-600" /> Approved websites (supplementary)
                      </div>
                      <div className="space-y-1">
                        {result.web.map((w, i) => (
                          <a key={i} href={w.url} target="_blank" rel="noreferrer" data-testid={`web-link-${i}`}
                            className="block rounded-lg px-2 py-2 transition-colors hover:bg-muted">
                            <div className="flex items-center gap-1 text-sm font-medium text-sky-700 dark:text-sky-300">
                              {w.title}<ArrowUpRight className="h-3 w-3" />
                            </div>
                            {w.snippet && <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{w.snippet}</div>}
                          </a>
                        ))}
                      </div>
                    </Card>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4">
          <Card className="p-5">
            <div className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <History className="h-4 w-4 text-sky-600" /> Recent across branches
            </div>
            <div className="space-y-1.5">
              {recent.length === 0 && <p className="text-xs text-muted-foreground">No searches yet.</p>}
              {recent.map((r, i) => (
                <button key={`${r.query}-${i}`} onClick={() => runSearch(r.query)} data-testid={`recent-${i}`}
                  className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted">
                  <span className="truncate">{r.query}</span>
                  <Badge variant="outline" className="shrink-0 gap-1 font-mono-c text-[10px]"><Zap className="h-2.5 w-2.5" />{r.hit_count}</Badge>
                </button>
              ))}
            </div>
          </Card>

          <Card className="mt-5 p-5">
            <div className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <FileText className="h-4 w-4 text-emerald-600" /> Active references
            </div>
            <div className="space-y-2">
              {activeResources.length === 0 && <p className="text-xs text-muted-foreground">No active references.</p>}
              {activeResources.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate text-foreground">{r.title}</span>
                  <Badge variant="outline" className="shrink-0 text-[10px]">#{r.precedence}</Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Verbatim excerpt drawer for references without an uploaded PDF */}
      <Sheet open={!!excerpt} onOpenChange={(o) => !o && setExcerpt(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg canda-scroll" data-testid="excerpt-drawer">
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-sky-600" /> {excerpt?.section || excerpt?.title}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            <Badge variant="outline" className="mb-3 font-mono-c text-xs">{excerpt?.title}{excerpt?.page != null ? ` · page ${excerpt.page}` : ""}</Badge>
            <div className="whitespace-pre-line rounded-xl border border-border bg-muted/40 p-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
              {excerpt?.text}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Upload the original PDF in the HOD portal to enable one-tap page viewing for this reference.
            </p>
          </div>
        </SheetContent>
      </Sheet>

      {pdfView && (
        <PdfViewer
          open={!!pdfView}
          onClose={() => setPdfView(null)}
          resourceId={pdfView.resourceId}
          page={pdfView.page}
          pdfPage={pdfView.pdfPage}
          title={pdfView.title}
        />
      )}
    </Layout>
  );
}
